import React, {useState, useEffect} from "react"
import Select, {SelectOption} from "@irati/ui/src/FormItem/Select"
import {Team, User as UserType} from "@irati/types"
import {canModifyUserMembership} from "@irati/identity/utils/team/membership"
import authentication from "@irati/auth"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import businessService from "@irati/identity/services/business"
import userService from "@irati/identity/services/users"

enum Roles {
  User = "user",
  Admin = "admin"
}

interface Props extends InjectIntl {
  /** @desc: If not provided, will use busines.rootTeamId */
  team: Team | null,
  user: UserType,
  onChange?: () => Promise<any>
}

const EditUserRole: React.FC<Props> = ({formatMessage, team, user, onChange}) => {
  const [loading, setLoading] = useState(false)
  const [role, setRole] = useState(Roles.User)
  const [options, setOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    setRole(user.role as Roles)
    setOptions(makeOptions(formatMessage, team, user))
  }, [])

  const handleChange = async(value: Roles) => {
    setLoading(true)
    setRole(value)
    let teamId = team?.uuid || ""
    if (!teamId) {
      const business = await businessService.get({businessId: user.businessId || user.tenant})
      teamId = business.rootTeamId
    }

    await makeRoleChange(teamId, user.uuid, value)
    if (onChange) {
      await onChange()
    }

    setLoading(false)
  }

  const normalisedRole = role === "super-admin" ? Roles.Admin : role

  return (
    <Select
      label="admin.components.userProfilePopUp.form.fields.select.label"
      value={normalisedRole}
      options={options}
      isSearchable={false}
      isClearable={false}
      isLoading={loading}
      name="rolesSelect"
      onChange={handleChange} />
  )
}

export default injectIntl(EditUserRole)

function makeOptions(formatMessage: FormatMessage, team: Team | null, user: UserType): SelectOption[] {
  const arr = [{value: Roles.User, label: formatMessage({id: "admin.roles.user"})}]
  if (!team || canModifyUserMembership(team, authentication.user(), user)
    && (authentication.isGlobalAdmin() || authentication.isUserGhost())) {
    arr.push({value: Roles.Admin, label: formatMessage({id: "admin.roles.admin"})})
  }

  return arr
}

function makeRoleChange(teamId: string, userId: string, role: Roles) {
  if (role === Roles.User) {
    return userService.makeUserBasic(teamId, userId)
  }
  return userService.makeUserGlobalAdmin(userId)
}
